import React, { forwardRef } from 'react';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import { Placement, PopupMenu } from 'shared/components/ui/popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';

import { LifeCycleList } from './life-cycle-list';
import { MoveList } from './move-list';
import { MenuGroups, SubMenu } from './types';

const itemRefsBase: Record<MenuGroups, Map<number, HTMLButtonElement>> = {
  [MenuGroups.Move]: new Map<number, HTMLButtonElement>(),
  [MenuGroups.Lifecycle]: new Map<number, HTMLButtonElement>(),
};

enum Direction {
  Next = 'next',
  Previous = 'previous',
}

export type GoalCardMultiSelectContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside' | 'position' | 'interactionType'
> & {
  goals: Goal[];
  onClose: () => void;
  initialActiveSubMenu?: SubMenu;
};

export const GoalCardMultiSelectContextMenu = forwardRef<
  HTMLDivElement,
  GoalCardMultiSelectContextMenuProps
>(({ goals, onClose, initialActiveSubMenu, ...rest }, ref) => {
  const [openSubMenu, setOpenSubMenu] = React.useState<SubMenu | undefined>(
    initialActiveSubMenu,
  );
  const itemRefs = React.useRef(itemRefsBase);

  const menuProps = {
    ...rest,
    position: 'bottom-start' as Placement,
    interactionType: InteractionType.Goal,
    onOutside: onClose,
  };

  const registerItem = React.useCallback(
    (group: MenuGroups, index: number, element: HTMLButtonElement | null) => {
      if (element) {
        itemRefs.current[group].set(index, element);
        return;
      }

      itemRefs.current[group].delete(index);
    },
    [],
  );

  const focusItem = React.useCallback((direction: Direction) => {
    const allRefs = [
      ...Array.from(itemRefs.current.move.entries()),
      ...Array.from(itemRefs.current.lifecycle.entries()),
    ].map(([_, ref]) => ref);

    if (!allRefs.length) {
      return;
    }

    const currentRef = document.activeElement as HTMLButtonElement;
    const currentIndex = allRefs.indexOf(currentRef);

    // If nothing focused, focus first item
    if (currentIndex === -1) {
      allRefs[0]?.focus();
      return;
    }

    const nextPos =
      (direction === Direction.Next
        ? currentIndex + 1
        : currentIndex - 1 + allRefs.length) % allRefs.length;
    allRefs[nextPos]?.focus();
  }, []);

  // capture + stopPropagation, like the task menu: without them an open sub-menu's own
  // arrow handler and this one both fire and focus jumps two rows.
  useKeysDown(
    ['ArrowDown'],
    (e) => {
      if (openSubMenu) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      focusItem(Direction.Next);
    },
    { capture: true },
  );

  useKeysDown(
    ['ArrowUp'],
    (e) => {
      if (openSubMenu) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      focusItem(Direction.Previous);
    },
    { capture: true },
  );

  // Move targets are per-goal (cycle prevention, current parent) — single selection only.
  const goalToMove = goals.length === 1 ? goals[0] : undefined;

  return (
    <>
      {!openSubMenu && (
        <PopupMenu ref={ref} {...menuProps}>
          {!!goalToMove && (
            <MoveList
              openSubMenu={setOpenSubMenu}
              registerItem={registerItem}
              registerGroup={MenuGroups.Move}
            />
          )}
          <LifeCycleList
            goals={goals}
            onClose={onClose}
            registerItem={registerItem}
            registerGroup={MenuGroups.Lifecycle}
          />
        </PopupMenu>
      )}

      {openSubMenu === SubMenu.MoveGoal && !!goalToMove && (
        <GoalTreeContextMenu
          id={goalToMove.id}
          onClose={onClose}
          {...menuProps}
        />
      )}
    </>
  );
});
